import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { Avatar } from '../components/Avatar';
import { AddPersonIcon, BackIcon, BellIcon, ChatIcon, CloseIcon, PhotosIcon, UploadIcon } from '../components/Icons';
import { Lightbox } from '../components/Lightbox';
import { PhotoGrid } from '../components/PhotoGrid';
import { BlockingProgress } from '../components/BlockingProgress';
import { GuestSharingSheet } from '../components/GuestSharingSheet';
import {
  api,
  photoPreviewUrl,
  type DropNotificationMode,
  type DropNotificationPreference,
  type FamilyDrop,
  type DropMember,
  type DropMembership,
  type Photo,
} from '../lib/api';

export function DropPage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [drop, setDrop] = useState<FamilyDrop | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<number | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadNote, setUploadNote] = useState<string | null>(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [savingNotifications, setSavingNotifications] = useState(false);
  const [showMembers, setShowMembers] = useState(false);
  const [showGuestSharing, setShowGuestSharing] = useState(false);
  const [guestSharingAvailable, setGuestSharingAvailable] = useState(false);
  const [guestPhotos, setGuestPhotos] = useState<Photo[]>([]);
  const [hasGuestConversation, setHasGuestConversation] = useState(false);
  const [pendingPromotion, setPendingPromotion] = useState<{ id: string; guestDisplayName: string } | null>(null);
  const [confirmingPromotion, setConfirmingPromotion] = useState(false);
  const [membership, setMembership] = useState<DropMembership | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  const [memberNote, setMemberNote] = useState<string | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [addToPhotos, setAddToPhotos] = useState(false);
  const [addToConversation, setAddToConversation] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const acknowledgedNotice = useRef<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const load = async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const result = await api.drop(id);
      setDrop((current) => ({
        ...result.drop,
        showPrivacyNotice: Boolean(
          result.drop.showPrivacyNotice
          || (current?.id === result.drop.id && current.showPrivacyNotice),
        ),
      }));
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not open this Family Drop.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [id]);
  useEffect(() => {
    let disposed = false;
    let refreshing = false;
    const refreshGuestContent = async () => {
      if (refreshing || document.visibilityState === 'hidden') return;
      refreshing = true;
      try {
        const capability = await api.guestDropCapabilities();
        if (disposed) return;
        setGuestSharingAvailable(capability.enabled);
        if (!capability.enabled) return;
        const external = await api.externalDrop(id).catch(() => null);
        if (disposed || !external) return;
        setHasGuestConversation(external.drop.grant.readConversation);
        const promotion = external.drop.pendingPromotions[0];
        setPendingPromotion(promotion ? { id: promotion.id, guestDisplayName: promotion.guestDisplayName } : null);
        setGuestPhotos(external.drop.media.filter((media) => media.state === 'available' && media.previewUrl).map((media) => ({
          postId: media.id, fileId: media.id, mediaId: media.id, name: media.name, path: '',
          contentType: media.contentType ?? 'application/octet-stream', size: media.size,
          lastModified: media.createdAt, hasPreview: true, owner: media.owner,
          isVideo: Boolean(media.contentType?.startsWith('video/')),
          previewUrl: media.previewUrl ?? undefined, downloadUrl: media.downloadUrl ?? undefined,
        })));
      } catch {
        if (!disposed) setGuestSharingAvailable(false);
      } finally {
        refreshing = false;
      }
    };
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') void refreshGuestContent();
    };

    void refreshGuestContent();
    const interval = window.setInterval(() => void refreshGuestContent(), 15_000);
    window.addEventListener('focus', refreshWhenVisible);
    document.addEventListener('visibilitychange', refreshWhenVisible);
    return () => {
      disposed = true;
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshWhenVisible);
      document.removeEventListener('visibilitychange', refreshWhenVisible);
    };
  }, [id]);

  useEffect(() => {
    if (!drop?.showPrivacyNotice || acknowledgedNotice.current === drop.id) return;
    acknowledgedNotice.current = drop.id;
    void api.acknowledgeDropPrivacyNotice(drop.id).catch(() => {
      // If acknowledgement fails, leaving the server row absent simply means
      // the helpful explanation will appear on the next visit too.
    });
  }, [drop?.id, drop?.showPrivacyNotice]);

  const loadMembership = async () => {
    if (!drop) return;
    setMembershipLoading(true);
    try {
      const result = await api.dropMembership(drop.id);
      setMembership(result.membership);
      setMembershipError(null);
    } catch (caught) {
      setMembershipError(caught instanceof Error ? caught.message : 'Could not load the people in this Drop.');
    } finally {
      setMembershipLoading(false);
    }
  };

  const openMembers = () => {
    setShowMembers(true);
    setSelectedMemberId(null);
    setMemberNote(null);
    setMembershipError(null);
    void loadMembership();
  };

  const chooseMember = (person: DropMember) => {
    if (addingMember) return;
    setSelectedMemberId(person.userId);
    setAddToPhotos(!person.inPhotos && Boolean(membership?.canAddPhotoMembers));
    setAddToConversation(!person.inConversation && Boolean(membership?.canAddConversationMembers));
    setMemberNote(null);
    setMembershipError(null);
  };

  const addMember = async () => {
    const person = membership?.people.find((candidate) => candidate.userId === selectedMemberId);
    if (!drop || !person || addingMember) return;
    const photoChange = addToPhotos && !person.inPhotos;
    const conversationChange = addToConversation && !person.inConversation;
    if (!photoChange && !conversationChange) return;
    setAddingMember(true);
    setMembershipError(null);
    try {
      const result = await api.addDropMember(drop.id, {
        userId: person.userId,
        addToPhotos: photoChange,
        addToConversation: conversationChange,
      });
      const destinations = [
        result.photoAdded ? 'photo sharing' : null,
        result.conversationAdded ? 'the conversation' : null,
      ].filter(Boolean);
      setMemberNote(`${person.displayName} was added to ${destinations.join(' and ')}.`);
      setSelectedMemberId(null);
      await Promise.all([loadMembership(), load(false)]);
    } catch (caught) {
      setMembershipError(caught instanceof Error ? caught.message : `Could not update ${person.displayName}’s access.`);
    } finally {
      setAddingMember(false);
    }
  };

  const upload = async (files: FileList | null) => {
    if (!files?.length || !drop || uploadProgress !== null) return;
    setUploadProgress(0);
    setUploadNote(null);
    try {
      const result = await api.uploadDropPhotos(drop.id, Array.from(files), setUploadProgress);
      const notes: string[] = [];
      if (result.uploaded.length > 0) notes.push(`${result.uploaded.length} ${result.uploaded.length === 1 ? 'item is' : 'items are'} now in ${drop.title}.`);
      if (result.rejected.length > 0) notes.push(`${result.rejected.length} could not be added.`);
      setUploadNote(notes.join(' '));
      await load(false);
    } catch (caught) {
      setUploadNote(caught instanceof Error ? caught.message : 'Photos and videos could not be added.');
    } finally {
      setUploadProgress(null);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const saveNotifications = async (preference: DropNotificationPreference) => {
    if (!drop || savingNotifications) return;
    setSavingNotifications(true);
    try {
      const result = await api.setDropNotifications(drop.id, preference);
      setDrop({ ...drop, notificationPreference: result.preference });
      setError(null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not change notifications.');
    } finally {
      setSavingNotifications(false);
    }
  };

  const confirmPromotion = async () => {
    if (!pendingPromotion || confirmingPromotion) return;
    setConfirmingPromotion(true); setError(null);
    try {
      await api.confirmGuestPromotion(id, pendingPromotion.id);
      setPendingPromotion(null);
      await load(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not connect your guest activity.');
    } finally { setConfirmingPromotion(false); }
  };

  if (loading) {
    return <div className="center-fill"><div className="spinner" role="status" aria-label="Loading Family Drop" /></div>;
  }

  if (!drop) {
    return (
      <div className="drop-unavailable">
        <PhotosIcon />
        <h1>This Drop is unavailable</h1>
        <p>{error ?? 'It may no longer be shared with you.'}</p>
        <button type="button" className="btn btn-primary" onClick={() => navigate('/drops')}>Back to Family Drops</button>
      </div>
    );
  }

  const preference = drop.notificationPreference;
  const isMuted = preference.mutedUntil === -1 || (preference.mutedUntil ?? 0) > Date.now();
  const allPhotos = [...drop.photos, ...guestPhotos.filter((guest) => !drop.photos.some((photo) => photo.mediaId === guest.mediaId))]
    .sort((left, right) => right.lastModified - left.lastModified);
  const heroPhoto = allPhotos[0];

  return (
    <>
      <header className="app-header drop-detail-header">
        <button type="button" className="chat-back" onClick={() => navigate('/drops')} aria-label="Back to Family Drops"><BackIcon /></button>
        <div><p className="eyebrow">Family Drop</p><h1>{drop.title}</h1></div>
        <button type="button" className={`drop-bell ${isMuted ? 'muted' : ''}`} onClick={() => setShowNotifications(true)} aria-label="Drop notification settings"><BellIcon /><span /></button>
      </header>

      <div className="drop-detail-page">
        <section className={`drop-detail-hero ${heroPhoto?.previewUrl ? 'has-photo' : ''}`}>
          {heroPhoto?.previewUrl && (
            <img className="drop-detail-hero-photo" src={photoPreviewUrl(heroPhoto, 1400)} alt="" />
          )}
          <div className="drop-detail-hero-smoke" aria-hidden="true" />
          <div className="drop-orbit" aria-hidden="true"><span /><span /><span /></div>
          <p>{formatDropDates(drop)}</p>
          <h2>{allPhotos.length > 0 ? `${allPhotos.length} ${allPhotos.length === 1 ? 'shared memory' : 'shared memories'}` : 'Ready for the first memory'}</h2>
          <div className="drop-audience-ribbon">
            {drop.canChat && <span><ChatIcon />{drop.participants.length} in conversation</span>}
            <span><PhotosIcon />{drop.photoAudience.label}</span>
            <button type="button" onClick={openMembers}><AddPersonIcon />People &amp; access</button>
            {guestSharingAvailable && drop.createdBy === user?.id && <button type="button" onClick={() => setShowGuestSharing(true)}><AddPersonIcon />Invite guests</button>}
          </div>
        </section>

        <nav className="drop-view-switcher" aria-label="Family Drop views">
          <button type="button" className="active" aria-current="page"><PhotosIcon />Photos & videos</button>
          {!hasGuestConversation && drop.canChat && drop.talkToken && (
            <button type="button" onClick={() => navigate(`/chat/${encodeURIComponent(drop.talkToken!)}`)}><ChatIcon />Conversation</button>
          )}
          {hasGuestConversation && <button type="button" onClick={() => navigate(`/drops/${encodeURIComponent(drop.id)}/conversation`)}><ChatIcon />Conversation</button>}
        </nav>

        {pendingPromotion && <div className="banner drop-promotion-banner" role="status"><div><strong>Connect your guest activity?</strong><p>An administrator linked the guest identity “{pendingPromotion.guestDisplayName}” to your account. Confirm only if that was you.</p></div><button type="button" className="btn btn-primary" disabled={confirmingPromotion} onClick={() => void confirmPromotion()}>{confirmingPromotion ? 'Connecting…' : 'Connect my activity'}</button></div>}

        <div className="page drop-photos-page">
          {error && <div className="banner error" role="alert"><p>{error}</p></div>}
          {uploadNote && <div className="banner"><p>{uploadNote}</p></div>}

          {drop.canViewPhotos ? (
            <>
              <div className="drop-photo-toolbar">
                <div><p className="eyebrow">Shared photos & videos</p><h2>{drop.title}</h2></div>
                {drop.canAddPhotos && (
                  <button type="button" className="btn btn-primary btn-sm" onClick={() => fileInput.current?.click()} disabled={uploadProgress !== null}>
                    <UploadIcon />Add photos & videos
                  </button>
                )}
              </div>
              <input ref={fileInput} hidden type="file" accept="image/*,video/*" multiple onChange={(event) => void upload(event.target.files)} />

              {allPhotos.length === 0 ? (
                <section className="drop-photos-empty">
                  <div><PhotosIcon /></div>
                  <h3>Nothing here yet</h3>
                  <p>Photos and videos added here—or intentionally included from the conversation—will collect in one place.</p>
                  {drop.canAddPhotos && <button type="button" className="btn btn-primary" onClick={() => fileInput.current?.click()}>Add the first photo or video</button>}
                </section>
              ) : (
                <PhotoGrid photos={allPhotos} onSelect={(_photo, index) => setSelected(index)} />
              )}
            </>
          ) : (
            <section className="drop-photos-empty private">
              <div><PhotosIcon /></div>
              <h3>The media has a different audience</h3>
              <p>You’re in the private conversation, but this photo and video collection is shared with {drop.photoAudience.label}.</p>
            </section>
          )}

          {drop.showPrivacyNotice && (
            <section className="drop-privacy-note">
              <ChatIcon />
              <div><strong>The conversation stays private</strong><p>Only the {drop.participants.length} selected people can see chat messages. Photos and videos are visible to {drop.photoAudience.label.toLowerCase()}.</p></div>
            </section>
          )}
        </div>
      </div>

      {selected !== null && (
        <Lightbox photos={allPhotos} index={selected} initialPreviewSize={400} onClose={() => setSelected(null)} onIndexChange={setSelected} />
      )}

      {showNotifications && (
        <div className="drop-notification-backdrop" role="presentation" onMouseDown={() => setShowNotifications(false)}>
          <section className="drop-notification-sheet" role="dialog" aria-modal="true" aria-labelledby="drop-notification-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="drop-notification-head">
              <div><p className="eyebrow">Just for this Drop</p><h2 id="drop-notification-title">Notifications</h2></div>
              <button type="button" onClick={() => setShowNotifications(false)} aria-label="Close"><CloseIcon /></button>
            </div>

            <div className="drop-notification-modes">
              <NotificationMode mode="all" current={preference.mode} title="All activity" detail="Conversation messages and new media" disabled={savingNotifications} onChoose={(mode) => void saveNotifications({ mode, mutedUntil: null })} />
              <NotificationMode mode="photos_mentions" current={preference.mode} title="Media and mentions" detail="Quiet the planning chatter" disabled={savingNotifications} onChoose={(mode) => void saveNotifications({ mode, mutedUntil: null })} />
              <NotificationMode mode="mentions" current={preference.mode} title="Mentions only" detail="Only when someone calls for you" disabled={savingNotifications} onChoose={(mode) => void saveNotifications({ mode, mutedUntil: null })} />
            </div>

            <div className="drop-mute-section">
              <p>Mute for a while</p>
              <div>
                <button type="button" disabled={savingNotifications} onClick={() => void saveNotifications({ ...preference, mutedUntil: Date.now() + 60 * 60 * 1000 })}>1 hour</button>
                <button type="button" disabled={savingNotifications} onClick={() => void saveNotifications({ ...preference, mutedUntil: tomorrowMorning() })}>Until tomorrow</button>
                {drop.endsAt && drop.endsAt > Date.now() && <button type="button" disabled={savingNotifications} onClick={() => void saveNotifications({ ...preference, mutedUntil: drop.endsAt })}>Until it ends</button>}
                <button type="button" disabled={savingNotifications} onClick={() => void saveNotifications({ ...preference, mutedUntil: -1 })}>Until I turn it on</button>
              </div>
              {isMuted && (
                <button type="button" className="drop-unmute" disabled={savingNotifications} onClick={() => void saveNotifications({ ...preference, mutedUntil: null })}>
                  Turn notifications back on
                </button>
              )}
            </div>
          </section>
        </div>
      )}

      {showMembers && (
        <DropMembersSheet
          drop={drop}
          membership={membership}
          loading={membershipLoading}
          error={membershipError}
          note={memberNote}
          selectedMemberId={selectedMemberId}
          addToPhotos={addToPhotos}
          addToConversation={addToConversation}
          saving={addingMember}
          onChoose={chooseMember}
          onChangePhotos={setAddToPhotos}
          onChangeConversation={setAddToConversation}
          onBack={() => setSelectedMemberId(null)}
          onSave={() => void addMember()}
          onClose={() => {
            if (addingMember) return;
            setShowMembers(false);
            setSelectedMemberId(null);
          }}
        />
      )}

      {showGuestSharing && <GuestSharingSheet dropId={drop.id} title={drop.title} onClose={() => setShowGuestSharing(false)} />}

      {uploadProgress !== null && <BlockingProgress title={`Preparing & adding media · ${Math.round(uploadProgress * 100)}%`} detail={`Optimizing videos and saving everything to ${drop.title}.`} />}
    </>
  );
}

function DropMembersSheet({
  drop,
  membership,
  loading,
  error,
  note,
  selectedMemberId,
  addToPhotos,
  addToConversation,
  saving,
  onChoose,
  onChangePhotos,
  onChangeConversation,
  onBack,
  onSave,
  onClose,
}: {
  drop: FamilyDrop;
  membership: DropMembership | null;
  loading: boolean;
  error: string | null;
  note: string | null;
  selectedMemberId: string | null;
  addToPhotos: boolean;
  addToConversation: boolean;
  saving: boolean;
  onChoose: (person: DropMember) => void;
  onChangePhotos: (selected: boolean) => void;
  onChangeConversation: (selected: boolean) => void;
  onBack: () => void;
  onSave: () => void;
  onClose: () => void;
}) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const selected = membership?.people.find((person) => person.userId === selectedMemberId) ?? null;
  const candidates = membership?.people.filter((person) => !person.isSelf && (!person.inPhotos || !person.inConversation)) ?? [];
  const changesSelected = Boolean(
    selected
    && ((addToPhotos && !selected.inPhotos) || (addToConversation && !selected.inConversation)),
  );

  useEffect(() => {
    closeRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !saving) onCloseRef.current();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [saving]);

  return (
    <div className="drop-notification-backdrop" role="presentation" onMouseDown={onClose}>
      <section className="drop-notification-sheet drop-members-sheet" role="dialog" aria-modal="true" aria-labelledby="drop-members-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="drop-notification-head">
          <div>
            <p className="eyebrow">{drop.title}</p>
            <h2 id="drop-members-title">{selected ? `Add ${selected.displayName}` : 'People & access'}</h2>
          </div>
          <button ref={closeRef} type="button" onClick={onClose} disabled={saving} aria-label="Close"><CloseIcon /></button>
        </div>

        {loading && !membership ? (
          <div className="drop-members-loading"><div className="spinner" role="status" aria-label="Loading Drop members" /></div>
        ) : selected && membership ? (
          <div className="drop-member-editor">
            <button type="button" className="drop-member-back" onClick={onBack} disabled={saving}>‹ All people</button>
            <div className="drop-member-identity">
              <Avatar userId={selected.userId} displayName={selected.displayName} size={58} />
              <div><strong>{selected.displayName}</strong><span>Choose where they should be added</span></div>
            </div>

            {error && <div className="banner error" role="alert"><p>{error}</p></div>}

            <div className="drop-member-access-options">
              <AccessOption
                icon={<PhotosIcon />}
                title="Photo sharing"
                detail={`Circle-wide · ${membership.photoAudience.label}`}
                checked={selected.inPhotos || addToPhotos}
                locked={selected.inPhotos}
                disabled={saving || selected.inPhotos || !membership.canAddPhotoMembers}
                onChange={onChangePhotos}
              />
              <AccessOption
                icon={<ChatIcon />}
                title="Conversation"
                detail="Private chat for this Drop only"
                checked={selected.inConversation || addToConversation}
                locked={selected.inConversation}
                disabled={saving || selected.inConversation || !membership.canAddConversationMembers}
                onChange={onChangeConversation}
              />
            </div>

            {addToPhotos && !selected.inPhotos && (
              <p className="drop-member-circle-warning">
                This adds {selected.displayName} to {membership.photoAudience.label}, including past and future photos, stories, and Drops shared with that circle.
              </p>
            )}
            {!membership.canAddPhotoMembers && !selected.inPhotos && (
              <p className="drop-member-unavailable">Photo-circle changes need the family cloud administrator to be signed in.</p>
            )}
            {!membership.canAddConversationMembers && !selected.inConversation && (
              <p className="drop-member-unavailable">Conversation changes need the Drop owner or a moderator to be signed in.</p>
            )}

            <button type="button" className="btn btn-primary btn-block" disabled={!changesSelected || saving} onClick={onSave}>
              {saving ? 'Updating access…' : `Add ${selected.displayName}`}
            </button>
          </div>
        ) : membership ? (
          <div className="drop-members-overview">
            {error && <div className="banner error" role="alert"><p>{error}</p></div>}
            {note && <div className="banner" role="status"><p>{note}</p></div>}

            <AudienceMembers
              icon={<PhotosIcon />}
              title="Photo sharing"
              detail={`${membership.photoAudience.label} · ${membership.photoMemberCount} ${membership.photoMemberCount === 1 ? 'person' : 'people'}`}
              members={membership.photoMembers}
              unavailable={!membership.photoMembersAvailable}
            />
            <AudienceMembers
              icon={<ChatIcon />}
              title="Conversation"
              detail={`${membership.conversationMembers.length} ${membership.conversationMembers.length === 1 ? 'person' : 'people'} · this Drop only`}
              members={membership.conversationMembers}
            />

            <div className="drop-members-candidates">
              <div><p className="eyebrow">Add or update</p><h3>Family members</h3></div>
              {candidates.length > 0 ? candidates.map((person) => (
                <button key={person.userId} type="button" onClick={() => onChoose(person)}>
                  <Avatar userId={person.userId} displayName={person.displayName} size={42} />
                  <span><strong>{person.displayName}</strong><small>{memberAccessLabel(person)}</small></span>
                  <span aria-hidden="true">›</span>
                </button>
              )) : (
                <p className="drop-members-complete">Everyone you can add is already in both parts of this Drop.</p>
              )}
            </div>
          </div>
        ) : (
          <div className="drop-members-loading">
            <p>{error ?? 'The people in this Drop could not be loaded.'}</p>
          </div>
        )}
      </section>
    </div>
  );
}

function AudienceMembers({ icon, title, detail, members, unavailable = false }: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  members: DropMember[];
  unavailable?: boolean;
}) {
  return (
    <section className="drop-members-audience">
      <div className="drop-members-audience-head">
        <span>{icon}</span>
        <div><h3>{title}</h3><p>{detail}</p></div>
      </div>
      {unavailable ? (
        <p className="drop-members-unavailable-list">Member names are temporarily unavailable.</p>
      ) : (
        <div className="drop-member-list">
          {members.map((person) => (
            <div key={person.userId}>
              <Avatar userId={person.userId} displayName={person.displayName} size={36} />
              <span>{person.isSelf ? 'You' : person.displayName}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function AccessOption({ icon, title, detail, checked, locked, disabled, onChange }: {
  icon: React.ReactNode;
  title: string;
  detail: string;
  checked: boolean;
  locked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={`${checked ? 'selected' : ''} ${locked ? 'locked' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(event) => onChange(event.target.checked)} />
      <span className="drop-member-access-icon">{icon}</span>
      <span><strong>{title}</strong><small>{locked ? 'Already included' : detail}</small></span>
      <span className="drop-member-check" aria-hidden="true">{checked ? '✓' : ''}</span>
    </label>
  );
}

function memberAccessLabel(person: DropMember): string {
  if (person.inPhotos && person.inConversation) return 'Photos and conversation';
  if (person.inPhotos) return 'In photo sharing · add to conversation';
  if (person.inConversation) return 'In conversation · add to photos';
  return 'Not in this Drop yet';
}

function NotificationMode({ mode, current, title, detail, disabled, onChoose }: {
  mode: DropNotificationMode;
  current: DropNotificationMode;
  title: string;
  detail: string;
  disabled: boolean;
  onChoose: (mode: DropNotificationMode) => void;
}) {
  return (
    <button type="button" className={current === mode ? 'selected' : ''} disabled={disabled} onClick={() => onChoose(mode)}>
      <span className="notification-radio" aria-hidden="true"><span /></span>
      <span><strong>{title}</strong><small>{detail}</small></span>
    </button>
  );
}

function formatDropDates(drop: FamilyDrop): string {
  if (!drop.startsAt && !drop.endsAt) return 'A shared family space';
  const options: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
  if (drop.startsAt && drop.endsAt) {
    return `${new Date(drop.startsAt).toLocaleDateString(undefined, options)} — ${new Date(drop.endsAt).toLocaleDateString(undefined, options)}`;
  }
  return new Date(drop.startsAt ?? drop.endsAt!).toLocaleDateString(undefined, { ...options, year: 'numeric' });
}

function tomorrowMorning(): number {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(8, 0, 0, 0);
  return tomorrow.getTime();
}
